/**
 * Security — MS33 public /security trust page.
 *
 * B2B essential. Buyers handing over employee birthday data + payment
 * details want to know how it's handled before they sign up. Most
 * SaaS sites bury this inside privacy policy; we surface it as a
 * dedicated page with the actual answers a buyer asks ("where does
 * the data live?", "what does Stripe see?", "what about employees
 * who leave?").
 *
 * Honest framing: we're early-stage, not SOC 2 certified yet, but
 * the foundations are in good shape — Supabase RLS scoping, Stripe
 * for payments, no card data on our servers, AU-region hosting,
 * minimal employee data collection. Page says all of that calmly.
 *
 * Structure:
 *   - Top nav (Logo + back link)
 *   - Hero: eyebrow + Heidi H1 + lede
 *   - 5 pillar sections with coral leading rules:
 *       1. Where data lives
 *       2. Payment security
 *       3. Employee data we collect (and don't)
 *       4. Account security
 *       5. Subprocessors + dependencies
 *   - "Working towards" — honest about what's not done yet (SOC 2)
 *   - Contact for security questions
 *   - Footer
 */

import { Link } from "react-router-dom";
import { ArrowUp, Database, Lock, Mail, ShieldCheck, UserCheck } from "lucide-react";
import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";

const displayFamily = "Newsreader, Georgia, serif";
const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const sansFamily = "DM Sans, sans-serif";

/* ─── Pillars ────────────────────────────────────────────────────── */

interface Pillar {
  id: string;
  number: string;
  icon: typeof Database;
  title: string;
  emphasis: string;
  body: string;
  bullets: string[];
}

const PILLARS: Pillar[] = [
  {
    id: "data",
    number: "01",
    icon: Database,
    title: "Where your data lives",
    emphasis: "lives",
    body:
      "Lembra runs on Supabase, hosted in AWS Singapore (ap-southeast-1). All employee and order data is row-level-security scoped. Your team's data is logically isolated from every other company's by Postgres RLS policies, enforced at the database layer.",
    bullets: [
      // MS347: was "Sydney (ap-southeast-2)" — actual region per
      // Supabase get_project is ap-southeast-1 (Singapore). Privacy
      // Policy § 6 was already saying Singapore; Security page +
      // chat prompts had drifted to a Sydney claim that didn't
      // match reality. Aligned to the truth.
      "Database: Supabase Postgres in AWS ap-southeast-1 (Singapore)",
      "Row-level-security on every table that holds company data",
      "Daily automated backups, 7-day point-in-time recovery",
      "All connections TLS 1.2+; no plaintext data in transit",
    ],
  },
  {
    id: "payments",
    number: "02",
    icon: Lock,
    title: "Payment security",
    emphasis: "security",
    body:
      "We never see your card. Payment details go directly to Stripe via their hosted checkout. Lembra's servers only receive a session reference. Stripe is PCI-DSS Level 1 certified.",
    bullets: [
      "Stripe-hosted checkout: card numbers never touch our servers",
      "Stripe is PCI-DSS Level 1 certified (highest tier)",
      "Webhook events signed and verified server-side",
      "Refunds and disputes routed through Stripe; we hold no balances",
    ],
  },
  {
    id: "employee-data",
    number: "03",
    icon: UserCheck,
    title: "Employee data we collect (and don't)",
    emphasis: "don't",
    body:
      "Lembra needs the minimum to do its job: name, birthday or work-anniversary date, dietary preferences, and a delivery address. That's it. We don't ask for tax info, salary, performance data, or anything else HR systems track.",
    bullets: [
      "Collected: name, celebration date, dietary preferences, delivery address",
      "Never collected: salary, tax info, performance data, manager notes",
      "Employees can be removed by an admin at any time. Their data is hard-deleted within 30 days.",
      "Recipients of gifts get one notification email; we don't add them to marketing",
    ],
  },
  {
    id: "accounts",
    number: "04",
    icon: ShieldCheck,
    title: "Account security",
    emphasis: "Account",
    body:
      "Authentication runs through Supabase Auth. Passwords are hashed with bcrypt, sessions use signed JWTs with 1-hour rotation, and we never store reset tokens in plaintext.",
    bullets: [
      "Supabase Auth (bcrypt password hashing)",
      "JWT sessions, 1-hour expiry with refresh-token rotation",
      "Password resets via signed time-limited links",
      "Magic-link sign-in supported; passwordless accounts never set a password",
      "Workspace admins can invite + remove team members at any time",
    ],
  },
  {
    id: "subprocessors",
    number: "05",
    icon: Database,
    title: "Subprocessors we depend on",
    emphasis: "depend",
    body:
      "Lembra is composed of a few well-known services. Each one has its own security posture you can read.",
    bullets: [
      "Supabase: database, auth, and storage (AWS Singapore, ap-southeast-1)",
      "Stripe: payment processing (PCI-DSS Level 1)",
      "Resend: transactional email (US-region)",
      "Anthropic: concierge chat LLM (data not retained for training per Anthropic's API terms)",
      "Sentry: error tracking (US-region; PII scrubbed via beforeSend hook)",
      "Lovable: frontend hosting (CDN edge)",
    ],
  },
];

/* ─── Page ───────────────────────────────────────────────────────── */

export default function Security() {
  return (
    <>
      <PageMeta
        title="Security. How we handle employee data and payments. Lembra."
        description="Where Lembra data lives, how Stripe handles payments, what employee data we collect (and don't), account security, and the subprocessors we depend on. Honest, calm, no buzzwords."
        path="/security"
      />

      <div style={{ background: "#FAF6F0", minHeight: "100vh" }}>
        {/* ── Top nav ── */}
        <header className="px-6 md:px-8 pt-6 pb-2">
          <div
            className="flex items-center justify-between"
            style={{ maxWidth: 1180, margin: "0 auto" }}
          >
            <Logo size="sm" />
            <Link
              to="/"
              className="text-sm font-medium text-navy/70 hover:text-coral"
              style={{ fontFamily: sansFamily }}
            >
              ← Back to lembra.com.au
            </Link>
          </div>
        </header>

        {/* ── Hero ── */}
        <section
          className="px-6 md:px-8"
          style={{
            paddingTop: "clamp(48px, 6vw, 72px)",
            paddingBottom: "clamp(32px, 4vw, 48px)",
          }}
        >
          <div style={{ maxWidth: 880, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(196,86,74,0.85)",
                marginBottom: 12,
              }}
            >
              Security · Trust
            </div>
            <h1
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.022em",
                color: "#1D3461",
                margin: 0,
                maxWidth: "20ch",
              }}
            >
              How we handle{" "}
              <em style={{ fontStyle: "italic", fontWeight: 500, color: "#C4564A" }}>
                your data
              </em>
              .
            </h1>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: 17,
                lineHeight: 1.55,
                color: "rgba(45,41,38,0.72)",
                marginTop: 18,
                marginBottom: 0,
                maxWidth: "62ch",
              }}
            >
              Plain answers to the questions a security-conscious buyer actually
              asks. No buzzwords, no certifications we haven't earned, no
              hand-waving.
            </p>
          </div>
        </section>

        {/* ── 5 Pillars ── */}
        <section
          className="px-6 md:px-8"
          style={{ paddingBottom: "clamp(56px, 7vw, 80px)" }}
        >
          <div style={{ maxWidth: 880, margin: "0 auto" }}>
            <div className="space-y-4">
              {PILLARS.map((pillar) => {
                const Icon = pillar.icon;
                const renderTitle = () => {
                  const idx = pillar.title.toLowerCase().indexOf(pillar.emphasis.toLowerCase());
                  if (idx === -1) return pillar.title;
                  return (
                    <>
                      {pillar.title.slice(0, idx)}
                      <em style={{ fontStyle: "italic", fontWeight: 500, color: "#C4564A" }}>
                        {pillar.title.slice(idx, idx + pillar.emphasis.length)}
                      </em>
                      {pillar.title.slice(idx + pillar.emphasis.length)}
                    </>
                  );
                };
                return (
                  <article
                    key={pillar.id}
                    className="rounded-2xl bg-white p-7 md:p-8"
                    style={{
                      border: "1px solid rgba(29,52,97,0.08)",
                      boxShadow: "0 4px 12px -8px rgba(29,52,97,0.12)",
                    }}
                  >
                    <div className="flex items-start gap-5">
                      {/* Icon chip + number */}
                      <div className="flex flex-col items-center shrink-0">
                        <span
                          className="inline-flex h-12 w-12 items-center justify-center rounded-full"
                          style={{ background: "rgba(196,86,74,0.08)" }}
                        >
                          <Icon size={20} strokeWidth={1.6} className="text-coral" />
                        </span>
                        <span
                          className="mt-2 text-[10px] uppercase tracking-[0.16em] text-navy/45"
                          style={{ fontFamily: monoFamily }}
                        >
                          {pillar.number}
                        </span>
                      </div>

                      <div className="flex-1">
                        <h2
                          style={{
                            fontFamily: displayFamily,
                            fontWeight: 500,
                            fontSize: "clamp(1.4rem, 2.4vw, 1.75rem)",
                            lineHeight: 1.18,
                            letterSpacing: "-0.012em",
                            color: "#1D3461",
                            margin: 0,
                          }}
                        >
                          {renderTitle()}
                        </h2>
                        <p
                          className="mt-3 text-sm leading-relaxed text-navy/75"
                          style={{ fontFamily: sansFamily, lineHeight: 1.65 }}
                        >
                          {pillar.body}
                        </p>
                        <ul className="mt-4 space-y-2">
                          {pillar.bullets.map((b) => (
                            <li
                              key={b}
                              className="flex items-start gap-2.5 text-sm text-navy/80"
                              style={{ fontFamily: sansFamily, lineHeight: 1.55 }}
                            >
                              <span
                                aria-hidden="true"
                                className="mt-2 h-1 w-1 shrink-0 rounded-full"
                                style={{ background: "#C4564A" }}
                              />
                              {b}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Working towards ── */}
        <section
          className="px-6 md:px-8"
          style={{
            background: "white",
            paddingTop: "clamp(56px, 7vw, 80px)",
            paddingBottom: "clamp(56px, 7vw, 80px)",
            borderTop: "1px solid rgba(29,52,97,0.06)",
            borderBottom: "1px solid rgba(29,52,97,0.06)",
          }}
        >
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(196,86,74,0.85)",
                marginBottom: 12,
              }}
            >
              Roadmap · Honest about what's not done
            </div>
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.75rem, 3vw, 2.2rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.018em",
                color: "#1D3461",
                margin: 0,
              }}
            >
              What we're{" "}
              <em style={{ fontStyle: "italic", color: "#C4564A" }}>working towards</em>.
            </h2>
            <p
              className="mt-5 text-sm leading-relaxed text-navy/75"
              style={{ fontFamily: sansFamily, lineHeight: 1.65 }}
            >
              Lembra is early-stage. We don't carry SOC 2 yet. That's a
              meaningful 12-18 month commitment we haven't started. If your
              team requires a formal certification before sign-up, tell us
              what you need and we'll either point you to where we are on
              the journey or pause the conversation honestly.
            </p>
            <ul
              className="mt-5 space-y-2 text-sm text-navy/80"
              style={{ fontFamily: sansFamily, lineHeight: 1.55 }}
            >
              <li className="flex items-start gap-2.5">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full" style={{ background: "#C4564A" }} />
                <span><strong className="text-navy">SOC 2 Type II:</strong> not started, planning a 2026 H2 review</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full" style={{ background: "#C4564A" }} />
                <span><strong className="text-navy">SAML / SSO for enterprise:</strong> on the roadmap, available on request when we get there</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full" style={{ background: "#C4564A" }} />
                <span><strong className="text-navy">Customer-managed encryption keys:</strong> not in scope for now, to revisit as enterprise demand grows</span>
              </li>
            </ul>
          </div>
        </section>

        {/* ── Contact ── */}
        <section
          className="px-6 md:px-8"
          style={{
            paddingTop: "clamp(56px, 7vw, 80px)",
            paddingBottom: "clamp(40px, 5vw, 64px)",
          }}
        >
          <div
            className="rounded-2xl bg-white p-7 md:p-8 text-center"
            style={{
              maxWidth: 720,
              margin: "0 auto",
              border: "1px solid rgba(29,52,97,0.08)",
            }}
          >
            <span
              className="inline-flex h-12 w-12 items-center justify-center rounded-full"
              style={{ background: "rgba(196,86,74,0.08)" }}
            >
              <Mail size={20} strokeWidth={1.6} className="text-coral" />
            </span>
            <h3
              className="mt-5"
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "1.5rem",
                lineHeight: 1.1,
                color: "#1D3461",
                margin: 0,
              }}
            >
              Security{" "}
              <em style={{ fontStyle: "italic", color: "#C4564A" }}>questions?</em>
            </h3>
            <p
              className="mt-3 text-sm text-navy/70"
              style={{ fontFamily: sansFamily }}
            >
              Email{" "}
              <a
                href="mailto:security@lembra.com.au"
                className="font-medium text-coral hover:underline underline-offset-2"
              >
                security@lembra.com.au
              </a>{" "}
              and we'll answer within one business day.
            </p>
          </div>
        </section>

        {/* ── Closing CTA ── */}
        <section
          className="px-6 md:px-8 text-center"
          style={{
            background: "#1D3461",
            color: "#FAF6F0",
            paddingTop: "clamp(56px, 7vw, 80px)",
            paddingBottom: "clamp(56px, 7vw, 80px)",
          }}
        >
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div
              aria-hidden="true"
              className="mx-auto mb-7"
              style={{ width: 48, height: 2, background: "#C4564A", borderRadius: 999 }}
            />
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.85rem, 3.4vw, 2.4rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.018em",
                color: "#FAF6F0",
                margin: "0 auto 14px",
                maxWidth: "22ch",
              }}
            >
              Ready when you{" "}
              <em style={{ fontStyle: "italic", color: "#F7E8A4" }}>are</em>.
            </h2>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: 16,
                lineHeight: 1.55,
                color: "rgba(250,246,240,0.72)",
                margin: "0 auto 28px",
                maxWidth: "44ch",
              }}
            >
              Brief our concierge with your first celebration. Or read the privacy
              policy + terms in full first.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/chat"
                className="inline-flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-medium text-white transition-all hover:bg-coral/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
                style={{
                  fontFamily: sansFamily,
                  boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
                }}
              >
                Open the concierge
                <ArrowUp size={14} strokeWidth={2.5} style={{ transform: "rotate(45deg)" }} />
              </Link>
              <Link
                to="/privacy"
                className="text-sm text-cream/80 underline-offset-4 hover:underline hover:text-cream"
                style={{ fontFamily: sansFamily }}
              >
                Read the privacy policy
              </Link>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={{ background: "#1D3461", padding: "0 0 36px" }}>
          <div
            className="mx-auto px-6 md:px-8 border-t border-white/10 pt-10 flex flex-col items-center gap-3"
            style={{ maxWidth: 1180 }}
          >
            <Logo size="sm" onDark />
            <p className="text-xs text-white/50" style={{ fontFamily: sansFamily }}>
              © 2026 Lembra. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
